import React, { useState } from "react";
import { Link } from "react-router-dom";
import { IUserWaitlistSummary } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";
import moment from "moment";

import { apiService } from "src/services/api.service";
import { Modal } from "src/components/Modal/Modal";
import Confirm from "src/components/Confirm/Confirm";
import CancelRegistrationModal from "src/routes/Account/UserWaitlistTab/CancelRegistrationModal";
import { toast } from "src/components/Toast/ToastManager";
import Icon from "src/components/Icon/Icon";

import styles from "./RecentEntries.module.scss";

const SECTION_SHOW_TYPES = [
    Constants.show_type.nosework,
    Constants.show_type.herding,
    Constants.show_type.country_rat_race,
];

// Container elements whose level is the whole story ("Veteran", not
// "Non-Licensed Classes · Veteran").
const NON_LICENSED_ELEMENTS: string[] = [
    Constants.dog_class_element.conformation_non_licensed,
    Constants.dog_class_element.performance_non_licensed_obedience,
];

const showTypeUsesSection = (showType: string, sanctioningClub: string): boolean => {
    if (SECTION_SHOW_TYPES.includes(showType)) return true;
    if (
        sanctioningClub === Constants.sanctioning_club.C_WAGS &&
        (showType === Constants.show_type.obedience || showType === Constants.show_type.rally_obedience)
    ) return true;
    return false;
};

interface RecentEntriesProps {
    /** Already trimmed to the rows to show (loaded once by the dashboard). */
    entries: IUserWaitlistSummary[];
    /** Called after a cancel/withdraw so the dashboard can reload entries. */
    onChanged: () => void;
}

// Course/class/seminar rows come back from the shared entries route too; they
// link to the course page and use the Account tab for cancellation.
const isEventEntry = (entry: IUserWaitlistSummary): boolean => {
    const kind = (entry as any).entry_kind;
    return !kind || kind === "event";
};

const entryLink = (entry: IUserWaitlistSummary): string | null => {
    if (!entry.event?.handle) return null;
    return isEventEntry(entry) ? `/e/${entry.event.handle}` : `/c/${entry.event.handle}`;
};

const RecentEntries: React.FC<RecentEntriesProps> = ({ entries, onChanged: fetchEntries }) => {
    const [cancelTarget, setCancelTarget] = useState<IUserWaitlistSummary | null>(null);
    const [withdrawTarget, setWithdrawTarget] = useState<IUserWaitlistSummary | null>(null);
    const [withdrawing, setWithdrawing] = useState(false);

    if (entries.length === 0) return null;

    const isPastShowDate = (entry: IUserWaitlistSummary): boolean =>
        new Date(entry.show_date) < new Date();

    const canCancel = (entry: IUserWaitlistSummary): boolean => {
        if (isPastShowDate(entry) || !isEventEntry(entry)) return false;
        return (
            entry.status === Constants.waitlist_status.registered ||
            entry.status === Constants.waitlist_status.refund_denied ||
            entry.status === Constants.waitlist_status.waitlist
        );
    };

    const getStatusLabel = (entry: IUserWaitlistSummary): string => {
        switch (entry.status) {
            case Constants.waitlist_status.registered:
                return "Registered";
            case Constants.waitlist_status.waitlist:
                return `Waitlist${entry.position ? ` #${entry.position}` : ""}`;
            case Constants.waitlist_status.refund_denied:
                return "Refund Denied";
            default:
                return entry.status ?? "";
        }
    };

    const getStatusClass = (entry: IUserWaitlistSummary): string => {
        switch (entry.status) {
            case Constants.waitlist_status.registered:
                return styles.statusRegistered;
            case Constants.waitlist_status.waitlist:
                return styles.statusWaitlist;
            case Constants.waitlist_status.refund_denied:
                return styles.statusDenied;
            default:
                return styles.statusDefault;
        }
    };

    const handleWithdrawConfirm = async () => {
        if (!withdrawTarget) return;
        setWithdrawing(true);
        try {
            await apiService.cancelUserWaitlistEntry(withdrawTarget._id);
            toast.show({
                title: "Withdrawn",
                content: `${withdrawTarget.call_name} has been withdrawn from the waitlist for ${withdrawTarget.show_name}.`,
                duration: 8000,
                type: "success",
            });
            setWithdrawTarget(null);
            fetchEntries();
        } catch (e: any) {
            toast.show({
                title: "Withdraw Failed",
                content: e?.response?.data?.message ?? "Something went wrong. Please try again.",
                duration: 8000,
                type: "fail",
            });
        } finally {
            setWithdrawing(false);
        }
    };

    // "Open · 6+ months": element (when it adds information), level, section.
    const classParts = (entry: IUserWaitlistSummary): string[] => {
        const parts: string[] = [];
        const element = entry.dog_class?.show_element;
        if (
            element &&
            element.toLowerCase() !== (entry.show_type || "").toLowerCase() &&
            !NON_LICENSED_ELEMENTS.includes(element)
        ) parts.push(element);
        if (entry.dog_class?.level) parts.push(entry.dog_class.level);
        if (
            entry.dog_class?.section &&
            entry.dog_class.section !== "None" &&
            showTypeUsesSection(entry.show_type, entry.sanctioning_club)
        ) parts.push(entry.dog_class.section);
        return parts;
    };

    return (
        <div className={styles.RecentEntries}>
            <div className={styles.header}>
                <div className={styles.title}>Recent entries</div>
            </div>
            <ul className={styles.list}>
                {entries.map((entry) => {
                    const cancellable = canCancel(entry);
                    const isWaitlist = entry.status === Constants.waitlist_status.waitlist;
                    const link = entryLink(entry);
                    const date = moment(entry.show_date);
                    const meta = [entry.call_name, entry.show_name].filter(Boolean).join(" · ");
                    const classLine = classParts(entry).join(" · ");

                    return (
                        <li key={`${entry._id}-${entry.dog_class?.show_element}-${entry.dog_class?.level}`} className={styles.item}>
                            <div className={styles.dateTile}>
                                <span className={styles.month}>{date.format("MMM")}</span>
                                <span className={styles.day}>{date.format("D")}</span>
                            </div>

                            <div className={styles.body}>
                                <div className={styles.topRow}>
                                    <span className={styles.typeLabel}>
                                        {[entry.sanctioning_club, entry.show_type].filter(Boolean).join(" ")}
                                    </span>
                                    <span className={`${styles.statusPill} ${getStatusClass(entry)}`}>
                                        {getStatusLabel(entry)}
                                    </span>
                                </div>
                                {link ? (
                                    <Link to={link} className={styles.eventName} title={entry.event.title}>
                                        {entry.event.title || "—"}
                                    </Link>
                                ) : (
                                    <span className={styles.eventName}>{entry.event?.title || "—"}</span>
                                )}
                                <div className={styles.metaRow}>
                                    <span className={styles.meta}>{meta}</span>
                                    {cancellable && (
                                        <button
                                            className={styles.cancelBtn}
                                            onClick={() =>
                                                isWaitlist
                                                    ? setWithdrawTarget(entry)
                                                    : setCancelTarget(entry)
                                            }
                                        >
                                            {isWaitlist ? "Withdraw" : "Cancel"}
                                        </button>
                                    )}
                                </div>
                                {classLine && <span className={styles.classLine} title={classLine}>{classLine}</span>}
                            </div>
                        </li>
                    );
                })}
            </ul>

            <Link to="/account?t=1" className={styles.viewAll}>
                <span>View all entries</span>
                <Icon type="caretRight" className={styles.viewAllIcon} />
            </Link>

            {/* Cancel registered entry modal */}
            {cancelTarget && (
                <Modal
                    isShown={!!cancelTarget}
                    hide={() => setCancelTarget(null)}
                    modalContent={
                        <CancelRegistrationModal
                            entry={{
                                show_registration_id: (cancelTarget as any).show_registration_id ?? cancelTarget._id,
                                call_name: cancelTarget.call_name ?? "",
                                show_type: cancelTarget.show_type,
                                show_name: cancelTarget.show_name,
                                show_date: String(cancelTarget.show_date),
                                show_element: cancelTarget.dog_class?.show_element ?? "",
                                level: cancelTarget.dog_class?.level,
                                section: cancelTarget.dog_class?.section,
                                entry_fee: null,
                                entry_platform_fee: null,
                                transaction_id: cancelTarget.transaction_id ?? null,
                                stripe_payment_intent_id: null,
                            }}
                            onSuccess={() => {
                                setCancelTarget(null);
                                fetchEntries();
                            }}
                            onCancel={() => setCancelTarget(null)}
                        />
                    }
                />
            )}

            {/* Withdraw from waitlist modal */}
            {withdrawTarget && (
                <Modal
                    isShown={!!withdrawTarget}
                    hide={() => setWithdrawTarget(null)}
                    modalContent={
                        <Confirm
                            message={`Are you sure you want to withdraw ${withdrawTarget.call_name} from the waitlist for ${withdrawTarget.show_type} — ${withdrawTarget.show_name}?`}
                            submitting={withdrawing}
                            buttonsDisabled={withdrawing}
                            onConfirm={handleWithdrawConfirm}
                            onCancel={() => setWithdrawTarget(null)}
                        />
                    }
                />
            )}
        </div>
    );
};

export default RecentEntries;
